import { AddMemberInput, MemberListType, TermData } from '@types';
import { roundTo2 } from '@utils/generic';
import { FeeCategoryDataType, TermsConfig, TermsList } from 'types/groups';

export const getTermEndDate = (startDate: string, duration: number) => {
  const termStartDate = new Date(startDate);
  if (process.env.REACT_APP_CUSTOM_TERM_DURATION === 'days') {
    return new Date(
      termStartDate.setDate(termStartDate.getDate() + Number(duration) - 1)
    ).toString();
  }
  return new Date(
    new Date(
      termStartDate.setMonth(termStartDate.getMonth() + Number(duration))
    ).setDate(termStartDate.getDate() - 1)
  ).toString();
};

export const getTermStartDateLimit = () => {
  const termDuration = 4;
  const termStartDate = new Date();
  if (process.env.REACT_APP_CUSTOM_TERM_DURATION === 'days') {
    return new Date(
      termStartDate.setDate(termStartDate.getDate() - Number(termDuration) + 1)
    ).toString();
  }

  return new Date(
    new Date(
      termStartDate.setMonth(termStartDate.getMonth() - Number(termDuration))
    ).setDate(1)
  ).toString();
};

export const getCustomTermDueDateCalendarMin = (
  previousTermDueDate?: string | null
): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!previousTermDueDate) return today;
  const prev = new Date(previousTermDueDate);
  if (Number.isNaN(prev.getTime())) return today;
  prev.setHours(0, 0, 0, 0);
  const dayAfterPrev = new Date(prev);
  dayAfterPrev.setDate(dayAfterPrev.getDate() + 1);
  return dayAfterPrev > today ? dayAfterPrev : today;
};

const addOneMonth = (baseDate: Date, targetDay: number): Date => {
  const next = new Date(baseDate);
  next.setMonth(next.getMonth() + 1, 1);
  const lastDay = new Date(
    next.getFullYear(),
    next.getMonth() + 1,
    0
  ).getDate();
  next.setDate(Math.min(targetDay, lastDay));
  return next;
};

/** Default due date for a new row: one month after the last term, clamped to month-end. */
export const getDefaultDueDateForNewTerm = (termsList: TermsList[]): string => {
  if (termsList.length === 0) {
    return new Date().toString();
  }
  const lastDue = new Date(termsList[termsList.length - 1]?.dueDate);
  if (Number.isNaN(lastDue.getTime())) {
    return new Date().toString();
  }
  const targetDay = lastDue.getDate();
  const nextDate = addOneMonth(lastDue, targetDay);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (nextDate < today ? today : nextDate).toString();
};

export const getInitialTermsList = (defaultTermLabel?: string): TermsList => ({
  id: Math.random().toString(16).slice(2),
  term_label: `${defaultTermLabel || 'Installment'} 1`,
  dueDate: new Date().toString(),
  dueAmount: ''
});

/** Two default rows for create / advanced custom term; both are non-deletable in UI. */
export const getDefaultTermsListForCreate = (
  defaultTermLabel?: string
): TermsList[] => {
  const label = defaultTermLabel || 'Installment';
  const first = getInitialTermsList(label);
  const second: TermsList = {
    ...getInitialTermsList(label),
    term_label: `${label} 2`,
    dueDate: getDefaultDueDateForNewTerm([first])
  };
  return [first, second];
};

export const initialTermsList = getInitialTermsList();

/** Split a total (currency units) across N terms; remainder cents go to first terms. */
export const splitTotalAcrossTerms = (
  total: number,
  termCount: number
): string[] => {
  if (termCount <= 0) return [];

  const totalCents = Math.round(Math.max(0, total) * 100);
  if (totalCents === 0) {
    return Array.from({ length: termCount }, () => '');
  }

  const eachGetsCents = Math.floor(totalCents / termCount);
  const leftoverCents = totalCents % termCount;

  const shares: string[] = [];
  for (let i = 0; i < termCount; i += 1) {
    const cents = eachGetsCents + (i < leftoverCents ? 1 : 0);
    shares.push(String(cents / 100));
  }
  return shares;
};

export const applyHeaderAmountSplitToTerms = (
  termsList: TermsList[],
  headerTotal: number
): TermsList[] => {
  const shares = splitTotalAcrossTerms(headerTotal, termsList.length);
  return termsList.map((term, i) => ({
    ...term,
    dueAmount: shares[i] ?? ''
  }));
};

export const applyHeaderAmountSplitToTermsWithFeeCategories = (
  termsList: TermsList[],
  headerTotal: number
): TermsList[] => {
  const shares = splitTotalAcrossTerms(headerTotal, termsList.length);
  return termsList.map((term, i) => ({
    ...term,
    dueAmount: shares[i] ?? '',
    feeCategoryData: term.feeCategoryData?.map((category, index) => ({
      ...category,
      amount: index === 0 ? shares[i] ?? '0' : '0'
    }))
  }));
};

const defaultTermCategories = (
  feeCategoryData: {
    id: number;
    label: string;
    value: string;
  }[]
) =>
  feeCategoryData.map((item: any) => ({
    branch_fee_category_id: item.id,
    account_detail_id: item.id,
    category: item.label,
    amount: item.value || '0'
  }));

export const onAddTerm = (
  termsList: TermsList[],
  feeCategories: FeeCategoryDataType[],
  defaultTermLabel?: string
) => {
  const id = Math.random().toString(16).slice(2);
  const newTerm: TermsList = {
    ...initialTermsList,
    id,
    term_label: `${defaultTermLabel || 'Installment'} ${termsList.length + 1}`,
    dueDate: getDefaultDueDateForNewTerm(termsList),
    feeCategoryData: defaultTermCategories(feeCategories)
  };

  return {
    term: [...termsList, newTerm]
  };
};

export const onTermDelete = (termId: string, termsList: TermsList[]) => {
  const finalTermsList = termsList.filter(
    (termDetails) => termDetails.id !== termId
  );

  const totalAmount = roundTo2(
    finalTermsList.reduce(
      (sum, termDetails) => sum + Number(termDetails.dueAmount),
      0
    )
  );

  return {
    termsList: finalTermsList,
    totalAmount
  };
};

export const isDateBefore = (
  date1: string | Date,
  date2: string | Date,
  shouldCheckEqual = false
) =>
  shouldCheckEqual
    ? new Date(new Date(date1).setHours(0, 0, 0, 0)) <=
      new Date(new Date(date2).setHours(0, 0, 0, 0))
    : new Date(new Date(date1).setHours(0, 0, 0, 0)) <
      new Date(new Date(date2).setHours(0, 0, 0, 0));

/** True when each term's due date is strictly after the previous (by row order). */
export const areCustomTermDueDatesAscending = (
  termsList?: TermsList[] | null
): boolean => {
  if (!termsList || termsList.length < 2) return true;
  for (let i = 1; i < termsList.length; i += 1) {
    const prevRaw = termsList[i - 1]?.dueDate;
    const curRaw = termsList[i]?.dueDate;
    if (
      prevRaw != null &&
      prevRaw !== '' &&
      curRaw != null &&
      curRaw !== '' &&
      !isDateBefore(prevRaw, curRaw)
    ) {
      return false;
    }
  }
  return true;
};

export const CUSTOM_TERM_DUE_DATE_ORDER_ERROR =
  'Each installment due date must be after the previous one';

export const getDuplicateTermLabelRowId = (
  termsList?: TermsList[] | null
): string | null => {
  if (!termsList || termsList.length < 2) return null;
  const seenLabels = new Set<string>();
  const duplicateTerm = termsList.find((term) => {
    const normalizedLabel = (term.term_label ?? '').trim().toLowerCase();
    if (!normalizedLabel) return false;
    if (seenLabels.has(normalizedLabel)) return true;
    seenLabels.add(normalizedLabel);
    return false;
  });
  return duplicateTerm?.id ?? null;
};

export const getCustomTermDuplicateLabelError = (defaultTermLabel?: string) =>
  `${defaultTermLabel || 'Installment'} label must be unique`;

export const getEmptyTermLabelRowId = (
  termsList?: TermsList[] | null
): string | null => {
  if (!termsList) return null;
  const emptyLabelTerm = termsList.find(
    (term) => !(term.term_label ?? '').trim()
  );
  return emptyLabelTerm?.id ?? null;
};

export const getCustomTermEmptyLabelError = (defaultTermLabel?: string) =>
  `${defaultTermLabel || 'Installment'} label cannot be empty`;

export const getTermDueCalendarSelectedDate = (
  dueDate: string,
  minSelectable: Date
): Date => {
  if (dueDate) {
    const chosen = new Date(dueDate);
    if (
      !Number.isNaN(chosen.getTime()) &&
      !isDateBefore(chosen, minSelectable)
    ) {
      return chosen;
    }
  }
  return new Date(minSelectable);
};

const isValidDate = (year: number, month: number, day: number) => {
  const date = new Date(year, month, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month &&
    date.getDate() === day
  );
};

const parseDateString = (dateString: string) => {
  // if first charecter is single quote then remove it
  // In csv some dates are enclosed in single quotes
  if (dateString.charAt(0) === "'") {
    dateString = dateString.slice(1);
  }

  // Check if date is in the format dd-mm-yyyy
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split('-').map(Number);
    if (isValidDate(year, month - 1, day)) {
      return {
        date: new Date(Number(year), Number(month) - 1, Number(day)),
        error: ''
      };
    }
    return { date: null, error: 'Invalid Date' };
  }
  // Check if date is in the format yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    if (isValidDate(year, month - 1, day)) {
      return {
        date: new Date(Number(year), Number(month) - 1, Number(day)),
        error: ''
      };
    }
    return { date: null, error: 'Invalid Date' };
  }

  // Check if date is in the format dd/mm/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split('/').map(Number);
    if (isValidDate(year, month - 1, day)) {
      return {
        date: new Date(Number(year), Number(month) - 1, Number(day)),
        error: ''
      };
    }
    return { date: null, error: 'Invalid Date' };
  }
  // check if date is the format yyyy/mm/dd
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('/').map(Number);
    if (isValidDate(year, month - 1, day)) {
      return {
        date: new Date(Number(year), Number(month) - 1, Number(day)),
        error: ''
      };
    }
  }

  return {
    date: null,
    error:
      'The date must be in one of the following formats: dd-mm-yyyy, yyyy-mm-dd, dd/mm/yyyy, or yyyy/mm/dd.'
  };
};

export const getCustomTermPastDueStatus = (
  termsList: TermsConfig[]
): { hasPastDue: boolean; allPastDue: boolean } => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const pastCount = termsList.filter((t) => {
    const d = new Date(t.due_date);
    d.setHours(0, 0, 0, 0);
    return d < now;
  }).length;
  return {
    hasPastDue: pastCount > 0,
    allPastDue: pastCount === termsList.length
  };
};

export const getMemberListPastDueStatus = (
  members: MemberListType[]
): { hasPastDue: boolean; allPastDue: boolean } => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  let totalTerms = 0;
  let pastCount = 0;
  members.forEach((member) => {
    member.termsList?.forEach((term) => {
      if (!term.dueDate) return;
      totalTerms += 1;
      const d = new Date(term.dueDate);
      d.setHours(0, 0, 0, 0);
      if (d < now) pastCount += 1;
    });
  });
  return {
    hasPastDue: pastCount > 0,
    allPastDue: totalTerms > 0 && pastCount === totalTerms
  };
};

export const zeroPastDueTermAmounts = (
  members: MemberListType[]
): MemberListType[] => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return members.map((member) => {
    if (!member.termsList?.length) return member;
    const memberData = {
      ...member,
      termsList: member.termsList.map((term) => {
        const d = new Date(term.dueDate);
        d.setHours(0, 0, 0, 0);
        if (d >= now) return term;
        return {
          ...term,
          dueAmount: '0',
          ...(term.feeCategoryData && {
            feeCategoryData: term.feeCategoryData.map((fc) => ({
              ...fc,
              amount: '0'
            }))
          })
        };
      })
    };

    const memberAmount = roundTo2(
      memberData.termsList.reduce(
        (acc, term) => acc + Number(term.dueAmount),
        0
      )
    );

    memberData.amount = memberAmount.toString();

    return memberData;
  });
};

export const customTermFileValidation = (
  feeCategories: string[],
  termsName: string[],
  member: AddMemberInput,
  index: number,
  minAmount: number,
  maxAmount: number,
  termsData?: TermData[]
) => {
  const errors: string[] = [];
  let totalAmount = 0;
  let isCategoryValueEmpty = false;
  let previousDueDate: Date | null = null;
  const today = new Date(new Date().setHours(0, 0, 0, 0));

  termsName.forEach((term, termIndex) => {
    let termAmount = 0;

    if (feeCategories.length === 0) {
      const termKey = `${term}_Amount`;
      const rawTermAmount = member[termKey as keyof AddMemberInput];
      isCategoryValueEmpty =
        rawTermAmount === undefined ||
        rawTermAmount === null ||
        String(rawTermAmount).trim() === '';
      termAmount = isCategoryValueEmpty ? 0 : Number(rawTermAmount);
      if (!isCategoryValueEmpty && Number.isNaN(termAmount)) {
        errors.push(`Row ${index + 1}: Invalid amount for ${term}`);
      }
    } else {
      feeCategories.forEach((category) => {
        const categoryKey = `${term}_${category.replace(/\s+/g, '')}`;
        const categoryValue = member[categoryKey as keyof AddMemberInput];
        isCategoryValueEmpty =
          categoryValue === undefined || categoryValue === null;

        if (categoryValue && Number(categoryValue).toLocaleString() === 'NaN') {
          errors.push(`Row ${index + 1}: Invalid amount for ${category}`);
        }
        termAmount += Number(categoryValue);
      });
      termAmount = roundTo2(termAmount);
    }

    if (!isCategoryValueEmpty && termAmount > maxAmount) {
      errors.push(
        `Row ${
          index + 1
        }: Term amount should be less than ${maxAmount} for ${term}`
      );
    }
    totalAmount += termAmount;

    const dueDateKey = `${term}_duedate`;
    const dueDateValue = member[dueDateKey as keyof AddMemberInput];

    if (dueDateValue) {
      const { date, error } = parseDateString(String(dueDateValue));

      if (error || !date) {
        errors.push(
          `Row ${index + 1}: Invalid date format for ${term} due date. ` +
            'The date must be in one of the following formats: ' +
            'dd-mm-yyyy, yyyy-mm-dd, dd/mm/yyyy, or yyyy/mm/dd.'
        );
      } else {
        const normalizedDate = new Date(date.setHours(0, 0, 0, 0));

        if (normalizedDate < today) {
          errors.push(
            `Row ${index + 1}: Due date for ${term} should not be a past date`
          );
        }

        if (previousDueDate && normalizedDate <= previousDueDate) {
          errors.push(
            `Row ${index + 1}: Due date for ${term} must be ` +
              'after the due date of the previous term'
          );
        }

        previousDueDate = normalizedDate;
      }
    } else {
      const groupDueDate = termsData?.[termIndex]?.due_date;
      if (groupDueDate) {
        const groupDate = new Date(groupDueDate);
        if (!Number.isNaN(groupDate.getTime())) {
          const normalizedGroupDate = new Date(groupDate.setHours(0, 0, 0, 0));

          if (previousDueDate && normalizedGroupDate <= previousDueDate) {
            errors.push(
              `Row ${index + 1}: Due date for ${term} must be ` +
                'after the due date of the previous term'
            );
          }

          previousDueDate = normalizedGroupDate;
        }
      } else if (termIndex === 0 || previousDueDate) {
        errors.push(`Row ${index + 1}: Due date is required for ${term}`);
      }
    }
  });

  if (!isCategoryValueEmpty && totalAmount !== 0 && totalAmount < minAmount) {
    errors.push(
      `Row ${index + 1}: Total term amount should be atleast ${minAmount}`
    );
  }

  return errors;
};
